/**
 * Search and highlighting utilities
 */

/**
 * Highlight search query in text
 * @param text - Text to highlight in
 * @param query - Search query (safe for non-string inputs)
 * @param highlightColor - Background color for highlight
 */
export const highlightMatch = (text: string, query: unknown, highlightColor = "#0D3665"): string => {
  // Ensure text is always a string
  if (typeof text !== "string") return "";

  // Safely normalize query to string
  const normalizedQuery = typeof query === "string" ? query.trim() : "";

  if (!normalizedQuery) return text;

  // Escape regex special chars to prevent invalid patterns
  const escapedQuery = normalizedQuery.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

  const regex = new RegExp(`(${escapedQuery})`, "gi");

  return text.replace(regex, `<mark style="background-color:${highlightColor};color:white;">$1</mark>`);
};

/**
 * Filter items by search query
 * @param items - Array of items to filter
 * @param query - Search query (can be any type, will be converted to string)
 * @param searchField - Field to search in (e.g., 'barcode')
 */
export const filterBySearch = <T extends Record<string, any>>(
  items: T[],
  query: unknown,
  searchField: keyof T,
): T[] => {
  // Safely convert query to string
  const queryString = query == null ? "" : String(query);
  const trimmedQuery = queryString.trim().toLowerCase();

  if (!trimmedQuery) return items;

  return items.filter((item) => item[searchField]?.toString().toLowerCase().includes(trimmedQuery));
};
